import { Grid, html } from 'gridjs'
import 'gridjs/dist/theme/mermaid.css'

import Sortable from 'sortablejs'
import { initFetchPhoneButton } from './prospect'
import { initScheduleButtonListeners } from './utils/bots_prospects/init_schedule_button_listeners'
import { initNoteButtonListeners } from './utils/bots_prospects/init_note_button_listeners'
import {initEmailComposerListeners} from './utils/bots_prospects/init_email_composer_button_listeners'
import { clearStatusCallingOverrides, getStatusFromStatusCallingCell } from './utils/bots_prospects/status_calling_overrides'

interface BotsProspectsData {
  bots_prospects: any[]
  total_count: number
}

interface GridState {
  instance: Grid | null
  rendered: boolean
  sortable?: Sortable | null
  columns: any[]
  rows: any[]
  totalCount?: number
}

const gridState: GridState = {
  instance: null,
  rendered: false,
  sortable: null,
  columns: [],
  rows: [],
  totalCount: undefined,
}

const fixedWidth = '240px'
const fixedWidthAction = '280px'

// Column resize state
let isResizing = false
let currentResizeColumn: HTMLElement | null = null
let startX = 0
let startWidth = 0
let hasResized = false

const COLUMN_ORDER_KEYS = {
  phone: 'phoneDashboardColumnOrder',
  default: 'defaultDashboardColumnOrder'
} as const

type DashboardType = 'phone' | 'default'

interface ColumnOrderManager {
  save(columns: { id: string }[], dashboardType: DashboardType): void
  load(dashboardType: DashboardType): string[] | null
}

const columnOrderManager: ColumnOrderManager = {
  save(columns: { id: string }[], dashboardType: DashboardType): void {
    if (dashboardType === 'default') return

    try {
      const columnIds = columns.map(col => col.id)
      const key = COLUMN_ORDER_KEYS[dashboardType]
      localStorage.setItem(key, JSON.stringify(columnIds))
    } catch (error) {
      console.error(`Failed to save column order for ${dashboardType}:`, error)
    }
  },

  load(dashboardType: DashboardType): string[] | null {
    if (dashboardType === 'default') return null

    try {
      const key = COLUMN_ORDER_KEYS[dashboardType]
      const stored = localStorage.getItem(key)
      return stored ? JSON.parse(stored) : null
    } catch (error) {
      console.error(`Failed to load column order for ${dashboardType}:`, error)
      return null
    }
  }
}

function reorderColumns(columns: { id: string }[], savedOrder: string[]): typeof columns {
  const columnMap = new Map(columns.map(col => [col.id, col]))
  const reordered: typeof columns = []

  savedOrder.forEach(id => {
    const col = columnMap.get(id)
    if (col) {
      reordered.push(col)
      columnMap.delete(id)
    }
  })

  columnMap.forEach(col => reordered.push(col))

  return reordered
}

function getDashboardType(isPhoneDashboard: boolean): DashboardType {
  return isPhoneDashboard ? 'phone' : 'default'
}

let isEmailComposerListenersInitialized = false;

// LocalStorage keys for column widths
const PHONE_DASHBOARD_COLUMNS_STORAGE_KEY = 'phone_dashboard_column_widths'
const BOTS_PROSPECTS_COLUMNS_STORAGE_KEY = 'bots_prospects_column_widths'

// Functions for saving and loading column widths
function saveColumnWidths(isPhoneDashboard: boolean, columns: any[]): void {
  try {
    const storageKey = isPhoneDashboard ? PHONE_DASHBOARD_COLUMNS_STORAGE_KEY : BOTS_PROSPECTS_COLUMNS_STORAGE_KEY
    const columnWidths: Record<string, string> = {}

    columns.forEach(column => {
      if (column.width) {
        columnWidths[column.id] = column.width
      }
    })

    localStorage.setItem(storageKey, JSON.stringify(columnWidths))
  } catch (error) {
    console.warn('Failed to save column widths:', error)
  }
}

function loadColumnWidths(isPhoneDashboard: boolean): Record<string, string> {
  try {
    const storageKey = isPhoneDashboard ? PHONE_DASHBOARD_COLUMNS_STORAGE_KEY : BOTS_PROSPECTS_COLUMNS_STORAGE_KEY
    const savedWidths = localStorage.getItem(storageKey)

    return savedWidths ? JSON.parse(savedWidths) : {}
  } catch (error) {
    console.warn('Failed to load column widths:', error)
    return {}
  }
}

function applyColumnWidths(columns: any[], savedWidths: Record<string, string>): any[] {
  return columns.map(column => {
    if (savedWidths[column.id]) {
      return { ...column, width: savedWidths[column.id] }
    }
    return column
  })
}

let BOTS_PROSPECTS_GRID_COLUMNS = [
  {
    id: 'name',
    name: 'Full Name',
    width: fixedWidth,
    sort: {
      enabled: true,
      compare: (a: { value: string, prospectData: any }, b: { value: string, prospectData: any }) => {
        try {
          return a.value.localeCompare(b.value);
        } catch {
          return 0;
        }
      }
    },
    formatter: (cell: { value: string, prospectData: any }) => {
      try {
        return html(formatExpandableCell(cell.value, "Full Name"));
      } catch {
        return "";
      }
    },
    attributes: (cell: { value: string, prospectData: any }) => {
      try {
        return {
          'data-prospect': JSON.stringify(cell.prospectData)
        }
      } catch {
        return {}
      }
    }
  },
  {
    id: 'schedule_reply',
    name: 'Actions',
    width: fixedWidthAction,
    sort: false,
  },
  {
    id: 'company',
    name: 'Current Company',
    width: fixedWidth,
    sort: {
      enabled: true,
      compare: (a: { name: string, domain: string }, b: { name: string, domain: string }) => {
        try {
          return a.name.localeCompare(b.name);
        } catch {
          return 0;
        }
      }
    },
    formatter: (cell: { name: string, domain: string }) => {
      try {
        return html(formatExpandableCellHTML(formatCompanyName(cell.name, cell.domain), 'Full Current Company'));
      } catch {
        return "";
      }
    }
  },
  {
    id: 'position',
    name: 'Current Title',
    width: fixedWidth,
    sort: true,
    formatter: (cell: string) => {
      try { return html(formatExpandableCell(cell, "Current Title")); } catch { return ""; }
    }
  },
  {
    id: 'location',
    name: 'Location',
    width: fixedWidth,
    sort: true,
    formatter: (cell: string) => {
      try { return html(formatExpandableCell(cell, "Location")); } catch { return ""; }
    }
  },
  {
    id: 'social',
    name: 'Link to social profiles',
    width: fixedWidth,
    sort: false,
    formatter: (cell: string) => {
      try {
        const links = cell ? JSON.parse(cell) : {};
        return html(formatSocialLinks(links));
      } catch {
        return "";
      }
    }
  },
  {
    id: 'first_contact',
    name: 'Time of first contact',
    width: fixedWidth,
    sort: {
      enabled: true
    },
    formatter: (cell: string) => {
      try { return html(formatExpandableCell(cell, "Time of first contact")); } catch { return ""; }
    }
  },
  {
    id: 'last_contact',
    name: 'Time of last contact',
    width: fixedWidth,
    sort: {
      enabled: true
    },
    formatter: (cell: string) => {
      try { return html(formatExpandableCell(cell, "Time of last contact")); } catch { return ""; }
    }
  },
  {
    id: 'status',
    name: 'Email Status',
    width: fixedWidth,
    sort: {
      enabled: true,
      compare: (a: { label: string }, b: { label: string }) => {
        try {
          return a.label.localeCompare(b.label);
        } catch {
          return 0;
        }
      }
    },
    formatter: (cell: { label: string, tooltip: string }) => {
      try { return html(formatStatus(cell)); } catch { return ""; }
    }
  },
  {
    id: 'reply',
    name: 'Reply',
    width: fixedWidth,
    sort: true,
    formatter: (cell: string) => {
      try { return html(formatExpandableCell(cell, "Full Reply")); } catch { return ""; }
    }
  },
  {
    id: 'response_date',
    name: 'Time of response',
    width: fixedWidth,
    sort: {
      enabled: true
    },
    formatter: (cell: string) => {
      try { return html(formatExpandableCell(cell, "Time of response")); } catch { return ""; }
    }
  },
  {
    id: 'bot',
    name: 'Campaign Name',
    width: fixedWidth,
    sort: {
      enabled: true,
      compare: (a: { id: string, name: string }, b: { id: string, name: string }) => {
        try {
          return a.name.localeCompare(b.name);
        } catch {
          return 0;
        }
      }
    },
    formatter: (cell: { id: string, name: string }) => {
      try { return html(formatExpandableCellHTML(formatBotName(cell.id, cell.name), 'Full Campaign Name')); } catch { return ""; }
    }
  },
  {
    id: 'user_name',
    name: 'User',
    width: fixedWidth,
    sort: true,
    formatter: (cell: string) => {
      try { return html(formatExpandableCell(cell, "User")); } catch { return ""; }
    }
  }
]

function extractStatusCallingValue(htmlStr: string): string {
  try {
    const doc = new DOMParser().parseFromString(htmlStr, 'text/html')
    const selected = doc.querySelector('option[selected]')
    return selected?.getAttribute('value') || selected?.textContent?.trim() || ''
  } catch {
    return typeof htmlStr === 'string' ? htmlStr : ''
  }
}

let PHONE_DASHBOARD_GRID_COLUMNS = [
  {
    id: 'name',
    name: 'Full Name',
    width: fixedWidth,
    sort: {
      enabled: true,
      compare: (a: { value: string, prospectData: any }, b: { value: string, prospectData: any }) => {
        try {
          return a.value.localeCompare(b.value);
        } catch {
          return 0;
        }
      }
    },
    formatter: (cell: { value: string, prospectData: any }) => {
      try {
        return html(formatExpandableCell(cell.value, "Full Name"));
      } catch {
        return "";
      }
    },
    attributes: (cell: { value: string, prospectData: any }) => {
      try {
        return {
          'data-prospect': JSON.stringify(cell.prospectData)
        }
      } catch {
        return {}
      }
    }
  },
  {
    id: 'status_calling',
    name: 'Status Calling',
    width: fixedWidth,
    sort: {
      enabled: true,
      compare: (a: string, b: string) => {
        try {
          return getStatusFromStatusCallingCell(a).localeCompare(getStatusFromStatusCallingCell(b))
        } catch {
          return 0
        }
      }
    },
    formatter: (cell: string) => {
      try { return html(cell) } catch { return "" }
    }
  },
  {
    id: 'status',
    name: 'Email Status',
    width: fixedWidth,
    sort: {
      enabled: true,
      compare: (a: { label: string }, b: { label: string }) => {
        try {
          return a.label.localeCompare(b.label);
        } catch {
          return 0;
        }
      }
    },
    formatter: (cell: { label: string, tooltip: string }) => {
      try { return html(formatStatus(cell)); } catch { return ""; }
    }
  },
  {
    id: 'first_contact',
    name: 'Time of first contact',
    width: fixedWidth,
    sort: {
      enabled: true
    },
    formatter: (cell: string) => {
      try { return html(formatExpandableCell(cell, "Time of first contact")); } catch { return ""; }
    }
  },
  {
    id: 'schedule_reply',
    name: 'Actions',
    width: fixedWidthAction,
    sort: false,
  },
  {
    id: 'company',
    name: 'Current Company',
    width: fixedWidth,
    sort: {
      enabled: true,
      compare: (a: { name: string, domain: string }, b: { name: string, domain: string }) => {
        try {
          return a.name.localeCompare(b.name);
        } catch {
          return 0;
        }
      }
    },
    formatter: (cell: { name: string, domain: string }) => {
      try {
        return html(formatExpandableCellHTML(formatCompanyName(cell.name, cell.domain), 'Full Current Company'));
      } catch {
        return "";
      }
    }
  },
  {
    id: 'position',
    name: 'Current Title',
    width: fixedWidth,
    sort: true,
    formatter: (cell: string) => {
      try { return html(formatExpandableCell(cell, "Current Title")); } catch { return ""; }
    }
  },
  {
    id: 'location',
    name: 'Location',
    width: fixedWidth,
    formatter: (cell: string) => {
      try { return html(formatExpandableCell(cell, "Location")); } catch { return ""; }
    }
  },
  {
    id: 'social',
    name: 'Link to social profiles',
    width: '191px',
    sort: false,
    formatter: (cell: string) => {
      try {
        const links = cell ? JSON.parse(cell) : {};
        return html(formatSocialLinks(links));
      } catch {
        return "";
      }
    }
  },
  {
    id: 'reply',
    name: 'Reply',
    width: fixedWidth,
    sort: true,
    formatter: (cell: string) => {
      try { return html(formatExpandableCell(cell, "Full Reply")); } catch { return ""; }
    }
  },
  {
    id: 'response_date',
    name: 'Time of response',
    width: fixedWidth,
    sort: {
      enabled: true
    },
    formatter: (cell: string) => {
      try { return html(formatExpandableCell(cell, "Time of response")); } catch { return ""; }
    }
  },
  {
    id: 'bot',
    name: 'Campaign Name',
    width: fixedWidth,
    sort: {
      enabled: true,
      compare: (a: { id: string, name: string }, b: { id: string, name: string }) => {
        try {
          return a.name.localeCompare(b.name);
        } catch {
          return 0;
        }
      }
    },
    formatter: (cell: { id: string, name: string }) => {
      try { return html(formatExpandableCellHTML(formatBotName(cell.id, cell.name), 'Full Campaign Name')); } catch { return ""; }
    }
  },
  {
    id: 'user_name',
    name: 'User',
    width: fixedWidth,
    sort: true,
    formatter: (cell: string) => {
      try { return html(formatExpandableCell(cell, "User")); } catch { return ""; }
    }
  }
]

function createJsonApiUrl(url: string): string {
  const apiUrl = new URL(url)

  apiUrl.searchParams.set('format', 'json')

  return apiUrl.toString()
}

const PHONE_DASHBOARD_PAGINATION_STORAGE_KEY = 'phone_dashboard_grid_pagination_v1'

function isPhoneDashboardTable(): boolean {
  const wrapper = document.getElementById('candidates-table')
  return wrapper?.getAttribute('data-phone-dashboard') === 'true'
}

function safeReadPhoneDashboardPagination(): { page: number; perPage: number } | null {
  try {
    const raw = window.localStorage.getItem(PHONE_DASHBOARD_PAGINATION_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    const page = Number(parsed?.page)
    const perPage = Number(parsed?.perPage)
    if (!Number.isFinite(page) || page <= 0) return null
    if (!Number.isFinite(perPage) || perPage <= 0) return null
    return { page, perPage }
  } catch {
    return null
  }
}

function safeWritePhoneDashboardPagination(pagination: { page: number; perPage: number }): void {
  try {
    window.localStorage.setItem(PHONE_DASHBOARD_PAGINATION_STORAGE_KEY, JSON.stringify(pagination))
  } catch {
    // ignore
  }
}

function getPhoneDashboardPaginationState(): { page: number; perPage: number } {
  const persisted = safeReadPhoneDashboardPagination()

  // Read current page from GridJS UI
  const wrapper = document.getElementById('candidates-table')
  const activePageEl = wrapper?.querySelector<HTMLElement>('.gridjs-pagination .gridjs-pages button.gridjs-currentPage')
  const page = activePageEl ? Number(activePageEl.innerText.trim()) : 1

  return {
    page: Number.isFinite(page) && page > 0 ? page : 1,
    perPage: persisted?.perPage ?? 100,
  }
}

function createServerConfig(baseUrl: string, columns: any[]) {
  return {
    url: createJsonApiUrl(baseUrl),
    then: (data: BotsProspectsData) => {
      gridState.totalCount = data.total_count || 0
      clearStatusCallingOverrides()

      const rows = data.bots_prospects.map((prospect: any) => {
        const prospectData = {
          id: prospect.id,
          botId: prospect.bot_id,
          name: prospect.name || 'N/A',
          location: prospect.location || 'N/A',
          companyName: prospect.company_name || 'N/A',
          companyDomain: prospect.company_domain || 'N/A',
          position: prospect.position || 'N/A',
          education: prospect.education || 'No education data.',
          skills: prospect.skills || [],
          languages: prospect.languages || [],
        };

        return {
          name: {
            value: prospect.name ?? '',
            prospectData,
          },
          schedule_reply: html(actionButtons(prospect.action_buttons_data)),
          company: {
            name: prospect.company_name,
            domain: prospect.company_domain,
          },
          position: prospect.position || '',
          location: prospect.location || 'N/A',
          social: JSON.stringify(prospect.social_links),
          first_contact: prospect.first_contact_date || '',
          last_contact: prospect.last_contact_date || '',
          response_date: prospect.response_date || '',
          status: prospect.status,
          status_calling: prospect.status_calling,
          reply: prospect.reply || '',
          bot: {
            id: prospect.bot_id,
            name: prospect.bot_name,
          },
          user_name: prospect.user_name || '',
        }
      })

      // Schedule DOM updates after render
      setTimeout(() => {
        const wrapper = document.getElementById('candidates-table')
        const tbody = wrapper?.querySelector('tbody')
        if (tbody && tbody.querySelectorAll('tr').length > 0) {
          initNoteButtonListeners(columns.length);
          initScheduleButtonListeners(columns.length);

          if (!isEmailComposerListenersInitialized) {
            initEmailComposerListeners();
            isEmailComposerListenersInitialized = true;
          }
        }
        $('[data-toggle="popover"]').popover({ html: true, sanitize: false, trigger: 'click' });
        initFetchPhoneButton();
        initSortableColumns();
        updateExpandableCells();
        $('.badge').tooltip();

        if (gridState.rendered && wrapper) {
          wrapper.scrollIntoView({ behavior: 'smooth', block: 'start' });
          const gridWrapper = wrapper.querySelector<HTMLElement>('.gridjs-wrapper');
          if (gridWrapper) gridWrapper.scrollTop = 0;
        }
      }, 300)

      gridState.rows = rows

      const wrapper = document.getElementById('candidates-table')
      const isPhoneDashboard = wrapper?.getAttribute('data-phone-dashboard') === 'true'
      const activeColumns = isPhoneDashboard ? gridState.columns : columns

      return rows.map(r => activeColumns.map(c => r[c.id]))
    },
    total: (data: BotsProspectsData) => data.total_count || 0
  }
}

function initSortableColumns() {
  if (gridState.sortable) {
    try { gridState.sortable.destroy() } catch { }
    gridState.sortable = null
  }

  const headerRow = document.querySelector('.gridjs-thead tr') as HTMLElement
  const bodyRows = document.querySelectorAll('.gridjs-tbody tr') as NodeListOf<HTMLElement>

  // Add resize handles to headers
  initColumnResizeHandles(headerRow)

  gridState.sortable = Sortable.create(headerRow, {
    animation: 150,
    scrollSensitivity: 100,
    forceFallback: false, // prevent buggy/freeze animations
    draggable: 'th.gridjs-th',
    handle: '.gridjs-th-content',
    onMove: (evt) => {
      if (evt.related == null || evt.dragged == null) {
        return
      }

      const oldIndex = evt.dragged?.cellIndex
      const newIndex = evt.related?.cellIndex
      if (oldIndex === newIndex) {
        return
      }

      const wrapper = document.getElementById('candidates-table')
      const isPhoneDashboard = wrapper?.getAttribute('data-phone-dashboard') === 'true'

      if (isPhoneDashboard) {
        return
      }

      bodyRows.forEach((row) => {
        const cells = row.children
        if (!cells[oldIndex] || !cells[newIndex]) {
          return
        }

        if (oldIndex < newIndex) {
          row.insertBefore(cells[oldIndex], cells[newIndex].nextSibling)
        } else {
          row.insertBefore(cells[oldIndex], cells[newIndex])
        }

        cells[oldIndex].innerHTML == ''
        cells[newIndex].innerHTML == ''
      })
    },
    onEnd: (evt) => {
      if (evt.oldIndex == null || evt.newIndex == null || evt.oldIndex == evt.newIndex) {
        return
      }

      const moved = gridState.columns.splice(evt.oldIndex, 1)[0]
      gridState.columns.splice(evt.newIndex, 0, moved)

      const isPhoneDashboard = isPhoneDashboardTable()

      // Save column order for all dashboard types
      const wrapper = document.getElementById('candidates-table')
      if (wrapper) {
        const dashboardType = getDashboardType(isPhoneDashboard)
        columnOrderManager.save(gridState.columns, dashboardType)
      }

      if (isPhoneDashboard) {
        const { page, perPage } = getPhoneDashboardPaginationState()
        const gridJsPage = Math.max(0, page - 1)

        safeWritePhoneDashboardPagination({ page, perPage })

        gridState.instance.updateConfig({
          columns: gridState.columns,
          pagination: {
            ...createPhoneDashboardPaginationConfig(gridJsPage),
            limit: perPage,
            page: gridJsPage,
            resetPageOnUpdate: false,
          },
          server: createServerConfig(window.location.href, gridState.columns),
        }).forceRender()

        const dashboardType = getDashboardType(isPhoneDashboard)
        columnOrderManager.save(gridState.columns, dashboardType)

        gridState.instance.forceRender()
        return
      }

      gridState.instance.updateConfig({
        columns: gridState.columns,
        server: createServerConfig(window.location.href, gridState.columns),
      }).forceRender();
    }
  })
}

// Initialize resize handles for column headers
function initColumnResizeHandles(headerRow: HTMLElement): void {
  if (!headerRow) return

  const headers = headerRow.querySelectorAll('th.gridjs-th') as NodeListOf<HTMLElement>

  headers.forEach((header, index) => {
    const existingHandle = header.querySelector('.resize-handle')
    if (existingHandle) {
      existingHandle.remove()
    }

    const resizeHandle = document.createElement('div')
    resizeHandle.className = 'resize-handle'
    resizeHandle.style.cssText = `
      position: absolute;
      top: 0;
      right: 0;
      width: 5px;
      height: 100%;
      cursor: col-resize;
      user-select: none;
      z-index: 10;
      background: transparent;
    `

    resizeHandle.addEventListener('mouseenter', () => {
      resizeHandle.style.background = 'rgba(0, 123, 255, 0.3)'
    })
    resizeHandle.addEventListener('mouseleave', () => {
      if (!isResizing) {
        resizeHandle.style.background = 'transparent'
      }
    })

    resizeHandle.addEventListener('mousedown', (e: MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()

      isResizing = true
      hasResized = false
      currentResizeColumn = header
      startX = e.pageX
      startWidth = header.offsetWidth

      resizeHandle.style.background = 'rgba(0, 123, 255, 0.5)'
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
      document.body.classList.add('resizing-column')
    })

    header.appendChild(resizeHandle)
  })

  const handleMouseMove = (e: MouseEvent) => {
    if (!isResizing || !currentResizeColumn) return

    const diff = e.pageX - startX

    if (Math.abs(diff) > 1) {
      hasResized = true
    }

    const newWidth = Math.max(50, startWidth + diff) // Minimum width of 50px

    currentResizeColumn.style.width = `${newWidth}px`
    currentResizeColumn.style.minWidth = `${newWidth}px`
    currentResizeColumn.style.maxWidth = `${newWidth}px`

    const headerIndex = Array.from(currentResizeColumn.parentElement!.children).indexOf(currentResizeColumn)
    const bodyRows = document.querySelectorAll('.gridjs-tbody tr') as NodeListOf<HTMLElement>

    bodyRows.forEach((row) => {
      const cell = row.children[headerIndex] as HTMLElement
      if (cell) {
        cell.style.width = `${newWidth}px`
        cell.style.minWidth = `${newWidth}px`
        cell.style.maxWidth = `${newWidth}px`
      }
    })

    const shortContents = document.querySelectorAll('.expandable-cell .short-content') as NodeListOf<HTMLElement>
    shortContents.forEach((content) => {
      const cell = content.closest('td') as HTMLElement
      if (cell) {
        const cellIndex = Array.from(cell.parentElement!.children).indexOf(cell)
        if (cellIndex === headerIndex) {
          // Adjust max-width for truncation, accounting for expand icon
          content.style.maxWidth = `${newWidth - 70}px`
        }
      }
    })
  }

  const handleMouseUp = () => {
    if (isResizing && currentResizeColumn) {
      const columnId = gridState.columns[Array.from(currentResizeColumn.parentElement!.children).indexOf(currentResizeColumn)]?.id
      const newWidth = currentResizeColumn.style.width

      if (columnId && newWidth) {
        const columnIndex = gridState.columns.findIndex(col => col.id === columnId)
        if (columnIndex !== -1) {
          gridState.columns[columnIndex].width = newWidth

          // Save column widths to localStorage after resize
          const wrapper = document.getElementById('candidates-table')
          if (wrapper) {
            const isPhoneDashboard = wrapper.getAttribute('data-phone-dashboard') === 'true'
            saveColumnWidths(isPhoneDashboard, gridState.columns)
          }
        }
      }

      const resizeHandle = currentResizeColumn.querySelector('.resize-handle') as HTMLElement
      if (resizeHandle) {
        resizeHandle.style.background = 'transparent'
      }

      setTimeout(() => {
        updateExpandableCells()
      }, 10)

      if (hasResized) {
        const columnToBlock = currentResizeColumn
        const clickBlocker = (e: MouseEvent) => {
          e.stopPropagation()
          e.preventDefault()
          columnToBlock.removeEventListener('click', clickBlocker, true)
        }
        columnToBlock.addEventListener('click', clickBlocker, true)

        setTimeout(() => {
          columnToBlock.removeEventListener('click', clickBlocker, true)
        }, 200)
      }
    }

    isResizing = false
    hasResized = false
    currentResizeColumn = null
    document.body.style.cursor = ''
    document.body.style.userSelect = ''
    document.body.classList.remove('resizing-column')
  }

  document.removeEventListener('mousemove', handleMouseMove)
  document.removeEventListener('mouseup', handleMouseUp)

  document.addEventListener('mousemove', handleMouseMove)
  document.addEventListener('mouseup', handleMouseUp)
}

function createPaginationConfig() {
  return {
    limit: 100,
    resetPageOnUpdate: false,
    server: {
      url: (prev: string, page: number, limit: number) => {
        const url = new URL(prev)

        url.searchParams.set('page', String(page + 1))
        url.searchParams.set('per_page', String(limit))

        return url.href
      }
    }
  }
}

function createPhoneDashboardPaginationConfig(initialPage?: number): any {
  const stored = safeReadPhoneDashboardPagination()
  const persistedPerPage = stored?.perPage ?? 100

  return {
    ...createPaginationConfig(),
    limit: persistedPerPage,
    page: initialPage ?? 0,
  }
}

function changePaginationLimit(newLimit: number): void {
  const wrapper = document.getElementById('candidates-table')
  if (!wrapper) return

  if (gridState.instance && gridState.rendered) {
    // Use the current gridState.columns to preserve user resized widths
    const columns = gridState.columns.length > 0 ? gridState.columns :
      (wrapper.getAttribute('data-phone-dashboard') === 'true' ? PHONE_DASHBOARD_GRID_COLUMNS : BOTS_PROSPECTS_GRID_COLUMNS)

    const isPhoneDashboard = wrapper.getAttribute('data-phone-dashboard') === 'true'

    if (isPhoneDashboard) {
      safeWritePhoneDashboardPagination({ page: 1, perPage: newLimit })
    }

    gridState.instance
      .updateConfig({
        pagination: isPhoneDashboard
          ? {
            ...createPhoneDashboardPaginationConfig(0),
            limit: newLimit,
            page: 0,
            resetPageOnUpdate: true,
          }
          : {
            limit: newLimit,
            resetPageOnUpdate: true,
            server: {
              url: (prev: string, page: number, limit: number) => {
                const url = new URL(prev)

                url.searchParams.set('page', String(page + 1))
                url.searchParams.set('per_page', String(limit))

                return url.href
              }
            }
          },
        server: createServerConfig(window.location.href, columns)
      })
      .forceRender()
  } else {
    initBotsProspectsGrid(newLimit)
  }
}

if (typeof window !== 'undefined') {
  (window as typeof window & { changePaginationLimit?: (limit: number) => void }).changePaginationLimit = changePaginationLimit
}

export function refreshGridData(): void {
  if (!gridState.instance || !gridState.rendered) return

  const wrapper = document.getElementById('candidates-table')
  if (!wrapper) return

  const isPhoneDashboard = wrapper.getAttribute('data-phone-dashboard') === 'true'
  const columns = gridState.columns.length > 0
    ? gridState.columns
    : (isPhoneDashboard ? PHONE_DASHBOARD_GRID_COLUMNS : BOTS_PROSPECTS_GRID_COLUMNS)

  if (isPhoneDashboard) {
    const { page, perPage } = getPhoneDashboardPaginationState()
    const gridJsPage = Math.max(0, page - 1)

    gridState.instance.updateConfig({
      columns,
      pagination: {
        ...createPhoneDashboardPaginationConfig(gridJsPage),
        limit: perPage,
        page: gridJsPage,
        resetPageOnUpdate: false,
      },
      server: createServerConfig(window.location.href, columns),
    }).forceRender()
  } else {
    gridState.instance.updateConfig({
      columns,
      server: createServerConfig(window.location.href, columns),
    }).forceRender()
  }
}

export function initBotsProspectsGrid(paginationLimit: number = 100): void {
  const wrapper = document.getElementById('candidates-table')
  if (!wrapper) return

  const isPhoneDashboard = wrapper.getAttribute('data-phone-dashboard') === 'true'

  if (isPhoneDashboard) {
    safeWritePhoneDashboardPagination({ page: 1, perPage: 100 })
  }

  const defaultColumns = isPhoneDashboard ? PHONE_DASHBOARD_GRID_COLUMNS : BOTS_PROSPECTS_GRID_COLUMNS
  const dashboardType = getDashboardType(isPhoneDashboard)
  const savedOrder = columnOrderManager.load(dashboardType)
  const savedWidths = loadColumnWidths(isPhoneDashboard)

  let columns = []

  if (savedOrder?.length) {
    columns = reorderColumns([...defaultColumns], savedOrder)
    columns = applyColumnWidths(columns, savedWidths)
  } else if (gridState.columns.length > 0) {
    columns = gridState.columns
  } else {
    columns = applyColumnWidths([...defaultColumns], savedWidths)
  }

  gridState.columns = columns

  const grid = new Grid({
    columns: gridState.columns,
    server: createServerConfig(window.location.href, gridState.columns),
    fixedHeader: true,
    pagination: {
      ...(isPhoneDashboard ? createPhoneDashboardPaginationConfig(0) : createPaginationConfig()),
      limit: paginationLimit,
    },
  })

  wrapper.innerHTML = ''

  grid.render(wrapper)

  grid.forceRender();

  // Store the grid instance for later use
  gridState.instance = grid
  gridState.rendered = true

  grid.config.store.subscribe((state: any) => {
    if (state.sort) {
      $('.summernote').summernote('destroy');

      const initialForms = wrapper.querySelectorAll('.all-candidates-schedule-form');
      initialForms.forEach((form) => {
        form.remove();
      });
      const scheduleButtons = wrapper.querySelectorAll('.schedule-button.active');
      scheduleButtons.forEach((button) => {
        button.classList.remove('active');
      });

      const noteRows = wrapper.querySelectorAll('.all-candidates-note-form');
      noteRows.forEach((row) => {
        row.remove();
      })
      const noteButtons = wrapper.querySelectorAll('.notes-button.active');
      noteButtons.forEach((button) => {
        button.classList.remove("active");
      })
    }
  });
}

function formatCompanyName(companyName: string, companyDomain: string): string {
  if (!companyName) return ''
  if (!companyDomain) return companyName

  const url = `https://${companyDomain}`

  return `
    <a
      href="${url}"
      target="_blank"
      rel="noopener noreferrer"
      class="text-primary"
      style="text-decoration: none;"
    >
      ${companyName}
    </a>
  `
}

function formatExpandableCellHTML(innerHTML: string, title: string = 'Full Data'): string {
  if (!innerHTML) return ''
  const escapedHTML = innerHTML.replace(/"/g, '&quot;').replace(/'/g, '&#39;')
  return `
    <div
      class="expandable-cell"
      style="display: flex; align-items: center; justify-content: space-between; width: 100%;"
    >
      <div
        class="short-content text-truncate"
        style="max-width: 150px;"
      >
        ${innerHTML}
      </div>
        <span
          class="view-full-data"
          data-content="${escapedHTML}"
          data-title="${title}"
          style="cursor: pointer; color: #007bff; font-size: 14px; display: none;"
          title="View full message">
          <i class="fas fa-expand-alt"></i>
      </span>
    </div>
  `
}

function formatExpandableCell(
  text: string,
  title: string = "Full Information",
): string {
  if (!text) return "";
  const escapedText = text
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<\/?.*?>/g, " ")
    .replace(/\r?\n/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const escapedDataContent = text
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

  return `
    <div
      class="expandable-cell"
      style="display: flex; align-items: center; justify-content: space-between; width: 100%;"
    >
      <div
        class="short-content text-truncate ${title == "Full Name" ? "view-full-profile" : ""}"
        style="max-width: 150px;"
      >
        ${escapedText}
      </div>
      <span
        class="view-full-data"
        data-content="${escapedDataContent}"
        data-title="${title}"
        style="cursor: pointer; color: #007bff; font-size: 14px; display: none;"
        title="View full message"
      >
        <i class="fas fa-expand-alt"></i>
      </span>
    </div>
  `;
}

function formatSocialLinks(links: any): string {
  if (!links || Object.keys(links).length === 0) return ''

  const socialPlatforms = [
    { key: 'linkedin', icon: 'fab fa-linkedin' },
    { key: 'github', icon: 'fab fa-github' },
    { key: 'stack_overflow', icon: 'fab fa-stack-overflow' },
    { key: 'meetup', icon: 'fab fa-meetup' },
    { key: 'hacker_news', icon: 'fab fa-hacker-news' },
    { key: 'facebook', icon: 'fab fa-facebook' },
    { key: 'twitter', icon: 'fab fa-twitter' },
    { key: 'quora', icon: 'fab fa-quora' },
    { key: 'angellist', icon: 'fab fa-angellist' }
  ]

  const linkElements = socialPlatforms
    .filter(platform => links[platform.key])
    .map(platform => {
      let icon = `<i class="${platform.icon}"></i>`;
      if (platform.key === 'twitter') {
        icon = `<img src="/x-twitter-brands-solid-full.svg" alt="Twitter" width="25px" height="25px" />`
      }

      return `<a href="${links[platform.key]}" target="_blank" class="personalize-external-link" style="display: flex; align-items: center;">
        ${icon}
      </a>`
    })
    .join('')

  return `<div class="personalize-icons text-truncate">${linkElements}</div>`
}

function formatStatus(status: { label: string, tooltip: string }): string {
  if (!status || !status.label) return ''

  const labels = status.label
  const tooltip = status.tooltip || ''

  const statusClasses = {
    'interested': 'badge-success',
    'responded': 'badge-primary',
    'opened': 'badge-info',
    'bounced': 'badge-danger',
    'contacted': 'badge-secondary',
    'not_contacted': 'badge-dark'
  } as const

  const badgeClass = statusClasses[labels.toLowerCase() as keyof typeof statusClasses] || 'badge-secondary'

  return `
    <div class="text-truncate">
      <span
        class="badge ${badgeClass}"
        style="
          padding: 4px 8px;
          font-size: 12px;
          border-radius: 6px;
        "
        title="${tooltip}"
        data-toggle="tooltip"
        data-placement="top"
      >
        ${labels}
      </span>
    </div>
  `
}

function actionButtons(data?: any): string {
  return `
    <div class="action-buttons d-flex text-truncate">
      ${data.schedule_button}
      ${data.linkedin_button}
      ${data.phone_button}
      ${data.note_button}
      ${data.email_button}
    </div>
  `
}

function formatBotName(botId: string, botName: string): string {
  if (!botId || !botName) return ''

  const path = `/bots/${botId}`

  return `<a href="${path}" class="text-primary" style="text-decoration: none;">${botName}</a>`
}

function updateExpandableCells() {
  const cells = document.querySelectorAll<HTMLElement>('.expandable-cell');
  cells.forEach(cell => {
    const content = cell.querySelector<HTMLElement>('.short-content');
    const span = cell.querySelector<HTMLElement>('.view-full-data');
    if (!content || !span) return;
    if (content.scrollWidth > content.clientWidth) {
      span.style.display = 'inline-block';
    } else {
      span.style.display = 'none';
    }
  });
}
